import { secp256k1 } from '@noble/curves/secp256k1.js';
import { Writer, concat, hash160, hex, sha256d } from './bytes';

export interface OutPoint {
  /** Transaction id, usual display (big-endian) hex. */
  txid: string;
  vout: number;
}

export function outPoint(txid: string, vout: number): OutPoint | null {
  const b = hex.decode(txid);
  if (!b || b.length !== 32 || !Number.isInteger(vout) || vout < 0 || vout > 0xffffffff) return null;
  return { txid: txid.toLowerCase(), vout };
}

function serializeOutPoint(w: Writer, o: OutPoint) {
  w.bytes(hex.decode(o.txid)!.slice().reverse()).u32(o.vout);
}

export interface TxInput {
  outpoint: OutPoint;
  sequence: number;
  scriptSig: Uint8Array;
  witness: Uint8Array[];
}

export interface TxOutput {
  value: number;
  scriptPubKey: Uint8Array;
}

export const input = (outpoint: OutPoint, sequence = 0xfffffffd): TxInput => ({
  outpoint,
  sequence,
  scriptSig: new Uint8Array(0),
  witness: [],
});

/** A COINWOW transaction (Bitcoin format, BIP141 witness serialisation). */
export class Transaction {
  constructor(
    public inputs: TxInput[],
    public outputs: TxOutput[],
    public version = 2,
    public locktime = 0,
  ) {}

  get hasWitness(): boolean {
    return this.inputs.some((i) => i.witness.length > 0);
  }

  serialize(includeWitness = true): Uint8Array {
    const witness = includeWitness && this.hasWitness;
    const w = new Writer().i32(this.version);
    if (witness) w.u8(0).u8(1);
    w.varint(this.inputs.length);
    for (const i of this.inputs) {
      serializeOutPoint(w, i.outpoint);
      w.varBytes(i.scriptSig).u32(i.sequence);
    }
    w.varint(this.outputs.length);
    for (const o of this.outputs) w.u64(o.value).varBytes(o.scriptPubKey);
    if (witness) {
      for (const i of this.inputs) {
        w.varint(i.witness.length);
        for (const item of i.witness) w.varBytes(item);
      }
    }
    return w.u32(this.locktime).done();
  }

  get hex(): string {
    return hex.encode(this.serialize());
  }
  get txid(): string {
    return hex.encode(sha256d(this.serialize(false)).reverse());
  }
  get wtxid(): string {
    return hex.encode(sha256d(this.serialize()).reverse());
  }
  get weight(): number {
    return this.serialize(false).length * 3 + this.serialize().length;
  }
  get vsize(): number {
    return Math.ceil(this.weight / 4);
  }

  /** BIP143 signature hash (SIGHASH_ALL) for a P2WPKH input. */
  segwitV0SigHash(index: number, publicKeyHash: Uint8Array, value: number): Uint8Array {
    const prevouts = new Writer();
    const sequences = new Writer();
    const outs = new Writer();
    for (const i of this.inputs) {
      serializeOutPoint(prevouts, i.outpoint);
      sequences.u32(i.sequence);
    }
    for (const o of this.outputs) outs.u64(o.value).varBytes(o.scriptPubKey);
    const inp = this.inputs[index];
    const pre = new Writer().i32(this.version).bytes(sha256d(prevouts.done())).bytes(sha256d(sequences.done()));
    serializeOutPoint(pre, inp.outpoint);
    // scriptCode for P2WPKH: OP_DUP OP_HASH160 <20> OP_EQUALVERIFY OP_CHECKSIG
    pre.varBytes(concat(Uint8Array.of(0x76, 0xa9, 0x14), publicKeyHash, Uint8Array.of(0x88, 0xac)));
    pre.u64(value).u32(inp.sequence).bytes(sha256d(outs.done())).u32(this.locktime).u32(1);
    return sha256d(pre.done());
  }

  /** Signs a P2WPKH input in place. */
  signP2WPKH(index: number, privateKey: Uint8Array, value: number) {
    const pub = secp256k1.getPublicKey(privateKey, true);
    const hash = this.segwitV0SigHash(index, hash160(pub), value);
    const sig = signLowR(hash, privateKey);
    this.inputs[index].scriptSig = new Uint8Array(0);
    this.inputs[index].witness = [concat(sig, Uint8Array.of(0x01)), pub];
  }
}

/**
 * ECDSA signature (DER), deterministic (RFC6979), low-S, and ground to a
 * low-R value exactly like COINWOW Core / Bitcoin Core: extra entropy is a
 * 32-byte buffer whose first 4 bytes are a little-endian counter.
 */
export function signLowR(hash32: Uint8Array, privateKey: Uint8Array): Uint8Array {
  let compact = secp256k1.sign(hash32, privateKey, { prehash: false, lowS: true });
  let counter = 0;
  while (compact[0] >= 0x80) {
    counter++;
    const extra = new Uint8Array(32);
    new DataView(extra.buffer).setUint32(0, counter, true);
    compact = secp256k1.sign(hash32, privateKey, { prehash: false, lowS: true, extraEntropy: extra });
  }
  const sig = secp256k1.Signature.fromBytes(compact, 'compact');
  return sig.toBytes('der');
}

export function verifySignature(derSig: Uint8Array, hash32: Uint8Array, publicKey: Uint8Array): boolean {
  try {
    return secp256k1.verify(derSig, hash32, publicKey, { prehash: false, format: 'der', lowS: true });
  } catch {
    return false;
  }
}
